import { create } from 'zustand'
import { combine } from 'zustand/middleware'

/** Keyed by `protectionVrn`: one sounding alarm per protection. */
export type AudibleEvents = Map<string, AudibleEvent>

export type AudibleEvent = {
	protectionVrn: string
	assetVrn: string
	endsAt: number
}

/** What one silence click covers: a whole asset (locations list) or named protections (alert card). */
export type AudibleScope = { kind: 'asset'; assetVrn: string } | { kind: 'protections'; protectionVrns: string[] }

const inScope = (event: AudibleEvent, scope: AudibleScope) =>
	scope.kind === 'asset' ? event.assetVrn === scope.assetVrn : scope.protectionVrns.includes(event.protectionVrn)

export const useAudibleEvents = create(
	combine({ events: new Map<string, AudibleEvent>() }, (set) => ({
		/** Adds newly sounding alarms. */
		arm: (incoming: AudibleEvent[]) =>
			set(({ events }) => ({ events: new Map([...events, ...incoming.map((event) => [event.protectionVrn, event] as const)]) })),
		/** Removes the given alarms, either because a speaker was clicked to silence them or because their window closed. */
		dismiss: (incoming: AudibleEvent[]) =>
			set(({ events }) => {
				const next = new Map(events)
				for (const { protectionVrn } of incoming) next.delete(protectionVrn)
				return next.size === events.size ? {} : { events: next }
			}),
		clear: () => set(({ events }) => (events.size === 0 ? {} : { events: new Map() })),
	})),
)

export const soundingInScope = (events: AudibleEvents, scope: AudibleScope) => {
	return [...events.values()].filter((event) => inScope(event, scope))
}

export const useAudibleEventsInScope = (scope: AudibleScope) =>
	soundingInScope(
		useAudibleEvents((s) => s.events),
		scope,
	)

export const useIsAssetAudible = (assetVrn: string) =>
	useAudibleEvents(({ events }) => {
		for (const event of events.values()) if (event.assetVrn === assetVrn) return true
		return false
	})
